<template>
  <RouterView v-if="isLoaded" name="NavBar" />
  <main v-if="isLoaded" class="d-flex main">
    <RouterView name="SideBar" />
    <RouterView v-slot="{ Component }" class="spacing-for-chat-icons">
      <Transition appear mode="out-in" name="fade">
        <!-- TODO revisit suspense after updating Vite, now it works only with 
        <Suspense timeout=0> but logs an error that it should wrap only a single node
        -->
        <component :is="Component" />
        <!-- <template #fallback>
            <loader class="position-static" />
          </template>
        </Suspense> -->
      </Transition>
    </RouterView>
    <div class="app-version text-muted px-2 py-1 fs-8">Version: {{ buildVersion }} </div>
    <ReloadModal />
    <AppConfirmModal />
    <Intercom v-if="intercomEnabled && authStore.getters.isAuthenticated" />
    <OnRamp v-if="shownOnRampWidget" />
  </main>
  <BaseLoader v-else class="position-static" is-full-page />
  <BasicOverlay :show="isSubSidebarOpen" />
</template>

<script lang="ts" setup>
import { computed, onErrorCaptured } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useDashboard } from '@/store/Dashboard'
import { useBusiness } from '@/store/Business'
import { useAuth } from '@/store/Auth'
import { notifyError } from '@/helpers/notification.helper'
import { BuildVersion } from '@/shared'
import { RouteList } from '@/routes/RouteList'
import { asyncComponentWrapper } from '@/composables/useReloadModal'

import ReloadModal from '@/components/Misc/ReloadModal.vue'
import AppConfirmModal from '@/components/Misc/AppConfirmModal.vue'
import OnRamp from '@/components/Misc/OnRampWrapper.vue'
const Intercom = asyncComponentWrapper(() => import('@/components/Misc/IntercomWrapper.vue'))
const BasicOverlay = asyncComponentWrapper(() => import('@/components/Misc/BasicOverlay.vue'))

const buildVersion = import.meta.env.VITE_BUILD_VERSION
const intercomEnabled = import.meta.env.VITE_INTERCOM_ENABLED

const authStore = useAuth()
const businessStore = useBusiness()
const dashboardStore = useDashboard()
const { t } = useI18n()
const route = useRoute()

const isLoaded = computed(() => {
  return ![
    RouteList.AUTH.LOGIN.NAME,
    RouteList.AUTH.PASSWORD.FORGOT_PASSWORD.NAME,
    RouteList.AUTH.PASSWORD.RESET_PASSWORD.NAME,
    RouteList.AUTH.REGISTRATION.STEPS.USER.NAME,
  ].includes(route.name as string)
    ? authStore.getters.isStoreLoaded &&
        businessStore.getters.isStoreLoaded &&
        dashboardStore.getters.isStoreLoaded
    : true
})

const isSubSidebarOpen = computed(() => dashboardStore.getters.isSubSidebarOpen)
const shownOnRampWidget = computed(
  () =>
    authStore.getters.isAuthenticated &&
    import.meta.env.VITE_BUILD_VERSION?.match(BuildVersion.Production),
)

// Stops the website from breaking in 'Main' and 'Production' environments
if (import.meta.env.VITE_BUILD_VERSION?.match(BuildVersion.Main || BuildVersion.Production)) {
  onErrorCaptured((error) => {
    // Let's show the errors in console for 'Main' environment, while keeping the non-breaking
    // behavior the same as in 'Production'
    if (import.meta.env.VITE_BUILD_VERSION?.match(BuildVersion.Main)) {
      console.error(error)
    }

    notifyError(t('Something went wrong. Please reload the page'))
    return false
  })
}
</script>
